import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);
export const ADMIN_SECTIONS_ORDER = [
  { section: 'dashboard', path: '/admin' },
  { section: 'vendas', path: '/admin/vendas' },
  { section: 'relatorios', path: '/admin/relatorios' },
  { section: 'monitoramento', path: '/admin/monitoramento' },
  { section: 'bi_metas', path: '/admin/bi-metas' },
  { section: 'vendedores', path: '/admin/vendedores' },
  { section: 'metas', path: '/admin/metas' },
  { section: 'grupos_metas', path: '/admin/grupos-metas' },
  { section: 'filtros', path: '/admin/filtros' },
  { section: 'variaveis', path: '/admin/variaveis' },
  { section: 'funcionarios', path: '/admin/funcionarios' },
  { section: 'modulos', path: '/admin/modulos' },
  { section: 'ia', path: '/admin/ia' },
  { section: 'usuarios', path: '/admin/usuarios', adminOnly: true },
  { section: 'minha_conta', path: '/admin/minha-conta' }
];

const DEFAULT_SECTIONS = ADMIN_SECTIONS_ORDER.map(item => item.section);

const normalizePermissions = (permissions = {}) => {
  const normalized = {};
  DEFAULT_SECTIONS.forEach((section) => {
    const p = permissions?.[section] || {};
    normalized[section] = {
      can_view: !!p.can_view,
      can_manage: !!p.can_manage
    };
  });
  return normalized;
};

const normalizeAllowedBranches = (branches) => {
  if (branches === null) return null;
  if (!Array.isArray(branches)) return [];
  return [...new Set(branches.map((b) => String(b).trim()).filter((b) => b !== ''))];
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);

  // Função para validar token
  const validateToken = useCallback(async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      // Sem token ou usuário, não é autenticado
      setLoading(false);
      return false;
    }

    try {
      // Tentar validar o token com o backend
      const response = await authService.validate();
      
      if (response.data?.user) {
        const normalizedUser = {
          ...response.data.user,
          permissions: normalizePermissions(response.data.user?.permissions || {}),
          allowed_branches: normalizeAllowedBranches(response.data.user?.allowed_branches)
        };
        setUser(normalizedUser);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setAuthError(null);
        setLoading(false);
        return true;
      }
    } catch (error) {
      // Token inválido ou expirado
      console.warn('Token inválido ou expirado:', error.message);
      setAuthError('Sessão expirada. Faça login novamente.');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
      setLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser({
          ...parsedUser,
          permissions: normalizePermissions(parsedUser?.permissions || {}),
          allowed_branches: normalizeAllowedBranches(parsedUser?.allowed_branches)
        });
        
        // Validar token em background
        validateToken();
      } catch (error) {
        console.error('Erro ao parsear usuário:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [validateToken]);

  // Validar token periodicamente (a cada 5 minutos)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const interval = setInterval(() => {
      validateToken();
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(interval);
  }, [validateToken]);

  const login = async (email, password) => {
    setAuthError(null);
    setRequires2FA(false);
    setTempToken(null);
    setPendingUser(null);
    try {
      const response = await authService.login(email, password);

      // Verificar se 2FA é necessário
      if (response.data?.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.data?.temp_token);
        setPendingUser(response.data?.user);
        return {
          success: true,
          requires2FA: true,
          user: response.data?.user
        };
      }

      const userData = response.data?.user;

      if (userData) {
        const normalizedUser = {
          ...userData,
          permissions: normalizePermissions(userData?.permissions || {}),
          allowed_branches: normalizeAllowedBranches(userData?.allowed_branches)
        };
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        localStorage.setItem('token', response.data?.token);
        localStorage.removeItem('redirectAfterLogin');
        setUser(normalizedUser);
        setAuthError(null);

        return { success: true };
      }

      return { success: false, message: 'Usuário não encontrado' };
    } catch (error) {
      // Verificar se é erro de autenticação (401)
      if (error.response?.status === 401) {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Email ou senha inválidos';
        setAuthError(errorMessage);
        return { success: false, message: errorMessage };
      }
      
      // Outros erros de rede ou servidor
      const errorMessage = 'Erro de conexão. Verifique se o servidor está rodando.';
      setAuthError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const verify2FAChallenge = async (code) => {
    setAuthError(null);
    try {
      const response = await authService.challenge2FA({
        temp_token: tempToken,
        code
      });

      const userData = response.data?.user;
      if (userData) {
        const normalizedUser = {
          ...userData,
          permissions: normalizePermissions(userData?.permissions || {}),
          allowed_branches: normalizeAllowedBranches(userData?.allowed_branches)
        };
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        localStorage.setItem('token', response.data?.token);
        localStorage.removeItem('redirectAfterLogin');
        setUser(normalizedUser);
        setRequires2FA(false);
        setTempToken(null);
        setPendingUser(null);
        setAuthError(null);

        return { success: true };
      }

      return { success: false, message: 'Erro ao verificar código' };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Código de verificação inválido';
      setAuthError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const cancel2FA = () => {
    setRequires2FA(false);
    setTempToken(null);
    setPendingUser(null);
    setAuthError(null);
  };

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('redirectAfterLogin');
    setUser(null);
    setAuthError(null);
    // Usar o basename do Router para redirecionar corretamente
    const basename = process.env.PUBLIC_URL || '/';
    window.location.href = `${basename}/login`;
  };

  const updateUser = (updatedUser) => {
    const normalizedUser = {
      ...updatedUser,
      permissions: normalizePermissions(updatedUser?.permissions || {}),
      allowed_branches: normalizeAllowedBranches(updatedUser?.allowed_branches)
    };
    setUser(normalizedUser);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
  };

  const hasSectionAccess = useCallback((sectionKey, action = 'view') => {
    if (!user) return false;
    if (user.role === 'admin') return true;

    if (sectionKey === 'minha_conta') return true;

    const permission = user.permissions?.[sectionKey];
    if (!permission) return false;

    if (action === 'manage') {
      return !!permission.can_manage;
    }
    return !!(permission.can_view || permission.can_manage);
  }, [user]);

  const getFirstAccessiblePath = useCallback(() => {
    if (!user) return '/login';
    for (const item of ADMIN_SECTIONS_ORDER) {
      if (item.adminOnly && user.role !== 'admin') {
        continue;
      }
      if (hasSectionAccess(item.section, 'view')) {
        return item.path;
      }
    }
    return '/admin/minha-conta';
  }, [user, hasSectionAccess]);

  const value = {
    user,
    loading,
    authError,
    login,
    logout,
    updateUser,
    validateToken,
    hasSectionAccess,
    getFirstAccessiblePath,
    isAuthenticated: !!user,
    requires2FA,
    tempToken,
    pendingUser,
    verify2FAChallenge,
    cancel2FA
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
